import { useQuery } from '@tanstack/react-query'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'

import axios from '@pepper/utils/src/function/axiosInstance'

import type { Topic } from '@pepper/utils/src/types/seo'

type QueryParamsFilter = {
  topic?: string[]
  keyword?: string[]
  existsCrrId?: boolean
  existsItemId?: boolean
  sort?: string
  lte?: number
  gte?: number
  exclude_position?: boolean
}

type Params = {
  projectId: string
  companyId: string
  page?: number
  limit?: number
  queryParamsFilter?: QueryParamsFilter
  enabled?: boolean
}

const getTopicAttributes = (
  crrId?: boolean,
  itemId?: boolean,
  excludePosition?: boolean
) => {
  const attributes: string[] = []
  if (crrId) {
    attributes.push(`crr_id:${true}`)
  } else if (crrId !== undefined) {
    attributes.push(`crr_id:${false}`)
  }

  if (itemId !== undefined && itemId) {
    attributes.push(`item_id:${true}`)
  } else if (itemId !== undefined) {
    attributes.push(`item_id:${false}`)
  }

  if (excludePosition) {
    attributes.push('self_top_position:false')
  }

  return attributes
}

const handleFetch = async ({
  projectId,
  companyId,
  page,
  limit,
  queryParamsFilter = {},
}: {
  projectId: string
  companyId: string
  page?: number
  limit?: number
  queryParamsFilter?: QueryParamsFilter
}) => {
  const topic = queryParamsFilter?.topic
  const keyword = queryParamsFilter?.keyword
  const exists = getTopicAttributes(
    queryParamsFilter?.existsCrrId,
    queryParamsFilter?.existsItemId,
    queryParamsFilter?.exclude_position
  )
  const { lte, gte } = queryParamsFilter

  const response = await axios.get(
    `${SEO_SERVICE_BASE_URL}/companies/${companyId}/topics`,
    {
      params: {
        project_id: projectId,
        page,
        limit,
        topic,
        keyword,
        exists,
        sort: queryParamsFilter?.sort,
        ...(lte !== undefined && {
          lte: `self_top_position:${lte}`,
        }),
        ...(gte !== undefined && {
          gte: `self_top_position:${gte}`,
        }),
      },
      paramsSerializer: {
        indexes: null,
      },
    }
  )
  return response?.data
}

export const useProjectTopics = ({
  projectId,
  companyId,
  page,
  limit,
  queryParamsFilter,
  enabled = false,
}: Params) => {
  return useQuery<
    {
      records: Topic[]
      total_records: number
    },
    {
      response: {
        status: number
      }
    }
  >({
    queryKey: [
      'project-topics',
      projectId,
      companyId,
      page,
      limit,
      queryParamsFilter,
    ],
    queryFn: () =>
      handleFetch({ projectId, companyId, page, limit, queryParamsFilter }),
    retry: false,
    enabled,
    refetchInterval: data => {
      const isProcessing = data?.records?.some(record =>
        ['pending', 'processing', 'error'].includes(
          record.keyword_generation_status
        )
      )
      const isGeneratingReport = data?.records?.some(
        record =>
          record?.crr &&
          ['pending', 'processing', 'error'].includes(
            record.crr.generation_status_name
          )
      )
      const shouldRefetch = isProcessing || isGeneratingReport
      return shouldRefetch ? 5000 : false
    },
  })
}
